import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { Card, CardHeader, Badge, Spinner } from '@tkpod/ui';
import {
  hasAnyPermission,
  ORDER_STATUS_LABELS,
  ORDER_STATUS_MERCHANT_VARIANT,
  getMerchantOrderStatusLabel,
  isMerchantMaskedDanger,
} from '@tkpod/shared';
import { useAuth } from '@/hooks/use-auth';
import { api } from '@/lib/api';
import { ProviderDashboardPage } from './ProviderDashboardPage';

interface DashboardStats {
  orders: {
    today: number;
    thisWeek: number;
    thisMonth: number;
    total: number;
  };
  revenue: {
    today: string;
    thisWeek: string;
    thisMonth: string;
  };
  organizations: {
    active: number;
    total: number;
  };
  providers: {
    active: number;
    total: number;
  };
  awaitingApproval: {
    total: number;
    olderThan3Days: number;
  };
  ordersByStatus: Array<{
    status: string;
    count: number;
  }>;
  recentOrders: Array<{
    id: string;
    orderNumber: string;
    organizationName: string;
    status: string;
    total: string;
    createdAt: string;
  }>;
}

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-gray-300',
  NEW: 'bg-blue-500',
  AWAITING_FULFILLMENT_REQUEST: 'bg-yellow-500',
  DESIGN_PENDING: 'bg-indigo-400',
  DESIGN_ATTACHED: 'bg-indigo-500',
  EXPORT_QUEUED: 'bg-purple-400',
  EXPORT_PROCESSING: 'bg-purple-500',
  EXPORTED: 'bg-purple-600',
  IN_PRODUCTION: 'bg-amber-500',
  PRODUCTION_DONE: 'bg-amber-600',
  READY_TO_SHIP: 'bg-teal-500',
  SHIPPED: 'bg-green-500',
  DELIVERED: 'bg-green-700',
  CANCELLED: 'bg-red-500',
  FAILED: 'bg-red-700',
  ON_HOLD: 'bg-orange-500',
  FRAUD_HOLD: 'bg-red-400',
  CANCEL_REQUESTED: 'bg-rose-400',
};

function formatCurrency(value: string): string {
  const num = parseFloat(value);
  if (isNaN(num) || num === 0) return '$0.00';
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}


export function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isProvider = !!user?.providerId;
  const userPermissions = user?.permissions ?? user?.currentOrganization?.permissions ?? [];
  const canViewAnalytics = hasAnyPermission(userPermissions, ['analytics:read', 'analytics:manage']);

  const statsQuery = useQuery<{ data: DashboardStats }>({
    queryKey: ['admin', 'dashboard', 'stats'],
    queryFn: () => api.get('/api/v1/admin/dashboard/stats'),
    refetchInterval: 60_000,
    enabled: !isProvider && canViewAnalytics,
  });

  if (isProvider) {
    return <ProviderDashboardPage />;
  }

  if (!canViewAnalytics) {
    return <WelcomeDashboard userPermissions={userPermissions} />;
  }

  const stats = statsQuery.data?.data;

  if (statsQuery.isLoading && !stats) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" label="Loading dashboard..." />
      </div>
    );
  }

  if (statsQuery.isError) {
    return (
      <Card>
        <div className="flex flex-col items-center justify-center gap-4 py-16">
          <p className="text-sm text-danger">
            Failed to load dashboard data. Please try again later.
          </p>
        </div>
      </Card>
    );
  }

  if (!stats) return null;

  const maxStatusCount = Math.max(...stats.ordersByStatus.map((s) => s.count), 1);

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card padding="md">
          <p className="text-sm text-text-secondary">Orders Today</p>
          <p className="mt-1 text-2xl font-semibold text-text-primary">{stats.orders.today}</p>
          <p className="mt-0.5 text-xs text-text-tertiary">
            {stats.orders.thisWeek} this week &middot; {stats.orders.total} total
          </p>
        </Card>

        <Card padding="md">
          <p className="text-sm text-text-secondary">Revenue Today</p>
          <p className="mt-1 text-2xl font-semibold text-text-primary">
            {formatCurrency(stats.revenue.today)}
          </p>
          <p className="mt-0.5 text-xs text-text-tertiary">
            {formatCurrency(stats.revenue.thisWeek)} this week &middot;{' '}
            {formatCurrency(stats.revenue.thisMonth)} this month
          </p>
        </Card>

        <Card padding="md">
          <p className="text-sm text-text-secondary">Organizations</p>
          <p className="mt-1 text-2xl font-semibold text-text-primary">
            {stats.organizations.active}
          </p>
          <p className="mt-0.5 text-xs text-text-tertiary">
            {stats.organizations.total} total
          </p>
        </Card>

        <Card padding="md">
          <p className="text-sm text-text-secondary">Providers</p>
          <p className="mt-1 text-2xl font-semibold text-text-primary">
            {stats.providers.active}
          </p>
          <p className="mt-0.5 text-xs text-text-tertiary">
            {stats.providers.total} total
          </p>
        </Card>
      </div>

      {/* Manual fulfillment approval aging alert */}
      {stats.awaitingApproval.total > 0 && (
        <Card padding="md">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-text-primary">
                  Orders awaiting fulfillment approval
                </p>
                {stats.awaitingApproval.olderThan3Days > 0 && (
                  <Badge variant="warning">
                    {stats.awaitingApproval.olderThan3Days} older than 3 days
                  </Badge>
                )}
              </div>
              <p className="mt-0.5 text-xs text-text-tertiary">
                {stats.awaitingApproval.total} order
                {stats.awaitingApproval.total === 1 ? '' : 's'} waiting for the
                merchant to request fulfillment in Shopify.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/orders?status=AWAITING_FULFILLMENT_REQUEST')}
              className="rounded-md border border-border bg-white px-3 py-1.5 text-xs font-medium text-text-primary transition-colors hover:bg-surface-secondary"
            >
              View orders
            </button>
          </div>
        </Card>
      )}

      {/* Order Status Distribution + Recent Orders */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Status Distribution */}
        <Card>
          <CardHeader
            title="Orders by Status"
            description="Current distribution of all orders."
          />
          <div className="mt-4 space-y-2">
            {stats.ordersByStatus.length === 0 ? (
              <p className="py-8 text-center text-sm text-text-tertiary">No orders yet.</p>
            ) : (
              stats.ordersByStatus
                .sort((a, b) => b.count - a.count)
                .map((entry) => (
                  <div key={entry.status} className="flex items-center gap-3">
                    <span className="w-36 truncate text-xs text-text-secondary">
                      {ORDER_STATUS_LABELS[entry.status] ?? entry.status.replace(/_/g, ' ')}
                    </span>
                    <div className="flex-1">
                      <div
                        className={`h-5 rounded ${STATUS_COLORS[entry.status] ?? 'bg-gray-400'}`}
                        style={{ width: `${(entry.count / maxStatusCount) * 100}%`, minWidth: '4px' }}
                      />
                    </div>
                    <span className="w-10 text-right text-xs font-medium text-text-primary">
                      {entry.count}
                    </span>
                  </div>
                ))
            )}
          </div>
        </Card>

        {/* Recent Orders */}
        <Card>
          <CardHeader
            title="Recent Orders"
            description="Latest 10 orders across all organizations."
          />
          <div className="mt-4 overflow-x-auto">
            {stats.recentOrders.length === 0 ? (
              <p className="py-8 text-center text-sm text-text-tertiary">No orders yet.</p>
            ) : (
              <table className="min-w-full divide-y divide-border text-sm">
                <thead>
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-text-secondary">
                      Order
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-text-secondary">
                      Organization
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-text-secondary">
                      Status
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-text-secondary">
                      Total
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-text-secondary">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {stats.recentOrders.map((order) => (
                    <tr key={order.id} className="hover:bg-surface-secondary transition-colors">
                      <td className="px-3 py-2 whitespace-nowrap font-medium text-text-primary">
                        {order.orderNumber}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-text-secondary">
                        {order.organizationName}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {/* Merchant-facing label; hover reveals the internal status */}
                        <Badge
                          variant={ORDER_STATUS_MERCHANT_VARIANT[order.status] ?? 'default'}
                          size="sm"
                          title={`Internal status: ${ORDER_STATUS_LABELS[order.status] ?? order.status.replace(/_/g, ' ')}`}
                        >
                          {getMerchantOrderStatusLabel(order.status)}
                        </Badge>
                        {/* Internal failure hidden by the merchant remap —
                            keep the ops signal visible. */}
                        {isMerchantMaskedDanger(order.status) && (
                          <Badge variant="danger" size="sm" className="ml-1.5">
                            {ORDER_STATUS_LABELS[order.status] ?? order.status.replace(/_/g, ' ')}
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-right text-text-primary">
                        {formatCurrency(order.total)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-right text-text-tertiary">
                        {formatDate(order.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

const QUICK_ACCESS_SECTIONS = [
  { permissions: ['order:list', 'order:manage'], label: 'Orders', path: '/orders', description: 'View and manage orders' },
  { permissions: ['product_template:list', 'product_template:manage'], label: 'Catalog', path: '/catalog', description: 'Browse product templates' },
  { permissions: ['organization:list', 'organization:manage'], label: 'Tenants', path: '/tenants', description: 'Manage organizations' },
  { permissions: ['user:list', 'user:manage'], label: 'Users', path: '/admin-users', description: 'Manage admin users' },
  { permissions: ['provider:list', 'provider:manage'], label: 'Providers', path: '/providers', description: 'Manage print providers' },
  { permissions: ['role:list', 'role:manage'], label: 'Roles', path: '/roles', description: 'Manage roles and permissions' },
] as const;

function WelcomeDashboard({ userPermissions }: { userPermissions: string[] }) {
  const navigate = useNavigate();
  const accessibleSections = QUICK_ACCESS_SECTIONS.filter((s) =>
    hasAnyPermission(userPermissions, [...s.permissions]),
  );

  return (
    <div className="space-y-6">
      <Card padding="md">
        <div className="py-6 text-center">
          <h2 className="text-xl font-semibold text-text-primary">Welcome to TK Admin</h2>
          <p className="mt-2 text-sm text-text-secondary">
            {accessibleSections.length > 0
              ? 'Here are the sections you have access to:'
              : 'Use the sidebar to navigate to your assigned sections.'}
          </p>
        </div>
      </Card>

      {accessibleSections.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {accessibleSections.map((section) => (
            <button
              key={section.path}
              type="button"
              onClick={() => navigate(section.path)}
              className="text-left rounded-lg transition-shadow hover:ring-2 hover:ring-primary/30 focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <Card padding="md">
                <p className="text-sm font-semibold text-text-primary">{section.label}</p>
                <p className="mt-1 text-xs text-text-secondary">{section.description}</p>
              </Card>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
